import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePayment } from '../hooks/usePayment';
import { useDigitalWallet } from '../hooks/useDigitalWallet';
import { dollarsToCents } from '../lib/stripe';
import { auth } from '../firebase';

// Zod schema for billing details validation
const billingDetailsSchema = z.object({
  name: z.string()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must be less than 100 characters')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  email: z.string()
    .email('Please enter a valid email address')
    .min(1, 'Email is required')
    .or(z.string().min(1, 'Email is required')), // More lenient for testing
  address: z.object({
    line1: z.string()
      .min(5, 'Address must be at least 5 characters')
      .max(200, 'Address must be less than 200 characters')
      .optional(),
    city: z.string()
      .min(2, 'City must be at least 2 characters')
      .max(100, 'City must be less than 100 characters')
      .optional(),
    state: z.string()
      .min(2, 'State must be at least 2 characters')
      .max(50, 'State must be less than 50 characters')
      .optional(),
    postal_code: z.string()
      .regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid US ZIP code')
      .optional(),
    country: z.string().default('US'),
  }),
});

export function PaymentForm({ orderData, userProfile, existingPaymentIntent, onPaymentSuccess, onPaymentError }) {
  // Check if delivery is selected
  const isDelivery = orderData?.fulfillment === 'deliver';
  const [cardComplete, setCardComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  
  const stripe = useStripe();
  const elements = useElements();
  const { createPaymentIntent, processPayment } = usePayment();
  
  // Digital wallet functionality
  const {
    canMakePayment,
    applePayAvailable,
    googlePayAvailable,
    loading: walletLoading,
    error: walletError,
    handlePaymentRequest
  } = useDigitalWallet(orderData, onPaymentSuccess, onPaymentError);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm({
    resolver: zodResolver(billingDetailsSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      address: {
        line1: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
      },
    },
  });

  // Watch form values for real-time updates
  const watchedValues = watch();

  // Auto-fill billing details from user profile
  useEffect(() => {
    console.log('[PaymentForm] useEffect triggered with userProfile:', userProfile);
    if (userProfile) {
      const formData = {
        name: userProfile.fullName || userProfile.displayName || '',
        email: userProfile.email || '',
        address: {
          line1: userProfile.address?.street || '',
          city: userProfile.address?.city || '',
          state: userProfile.address?.state || '',
          postal_code: userProfile.address?.zip || '',
          country: 'US',
        },
      };
      console.log('[PaymentForm] Resetting form with data:', formData);
      reset(formData);
    }
  }, [userProfile, reset]);

  // Use existing payment intent from tax calculation, or create one if needed
  useEffect(() => {
    console.log('[PaymentForm] 🔵 useEffect for payment intent initialization triggered:', {
      hasPaymentIntent: !!paymentIntent,
      paymentIntentId: paymentIntent?.id,
      hasExistingPaymentIntent: !!existingPaymentIntent,
      existingPaymentIntentId: existingPaymentIntent?.id,
      existingPaymentIntentAmount: existingPaymentIntent?.amount,
      creatingIntent,
      stripeReady: !!stripe,
      orderDataTotal: orderData?.total
    });

    const initializePaymentIntent = async () => {
      // If we already have a payment intent, use it
      if (paymentIntent) {
        console.log('[PaymentForm] ✅ Already have payment intent, skipping initialization');
        return;
      }

      // If we have an existing payment intent from tax calculation, check if we can reuse it
      if (existingPaymentIntent && existingPaymentIntent.client_secret) {
        console.log('[PaymentForm] Found existing payment intent from tax calculation:', {
          id: existingPaymentIntent.id,
          amount: existingPaymentIntent.amount,
          hasClientSecret: !!existingPaymentIntent.client_secret
        });
        
        // Check if the amounts match (might differ due to discount)
        const expectedAmount = dollarsToCents(orderData.total);
        const existingAmount = existingPaymentIntent.amount;
        
        if (existingAmount === expectedAmount) {
          // Amounts match, we can reuse it
          console.log('[PaymentForm] Reusing existing payment intent - amounts match');
          setPaymentIntent(existingPaymentIntent);
          return;
        } else {
          console.log('[PaymentForm] Payment intent amounts differ, will create new one:', {
            existing: existingAmount,
            expected: expectedAmount,
            difference: expectedAmount - existingAmount
          });
          // Amounts don't match (likely due to discount), we need to create a new one
          // The old one will expire naturally, we don't need to cancel it
        }
      }

      // Only create if we don't already have one and Stripe is ready
      if (creatingIntent || !stripe || !orderData?.total) {
        return;
      }

      try {
        setCreatingIntent(true);
        console.log('[PaymentForm] Creating new payment intent with orderData:', {
          total: orderData.total,
          amount: dollarsToCents(orderData.total),
          itemsCount: orderData.items?.length,
          hadExistingIntent: !!existingPaymentIntent
        });

        const intent = await createPaymentIntent({
          ...orderData,
          amount: dollarsToCents(orderData.total),
          currency: 'usd',
        });

        console.log('[PaymentForm] New payment intent created:', {
          id: intent.id,
          amount: intent.amount,
          hasClientSecret: !!intent.client_secret
        });

        setPaymentIntent(intent);
      } catch (err) {
        console.error('[PaymentForm] Failed to create payment intent:', err);
        setError(err.message || 'Failed to initialize payment. Please try again.');
      } finally {
        setCreatingIntent(false);
      }
    };

    initializePaymentIntent();
  }, [stripe, orderData, paymentIntent, creatingIntent, existingPaymentIntent, createPaymentIntent]);

  // Debug: Check if Stripe hooks are working
  console.log('PaymentForm - stripe available:', !!stripe);
  console.log('PaymentForm - elements available:', !!elements);
  console.log('PaymentForm - initial billing details:', watchedValues);
  console.log('PaymentForm - initial form state:', { cardComplete, isValid });
  console.log('PaymentForm - isDelivery:', isDelivery);
  console.log('PaymentForm - isFormComplete check:', {
    cardComplete,
    name: watchedValues.name,
    email: watchedValues.email,
    emailValid: watchedValues.email && watchedValues.email.includes('@'),
    isFormComplete: cardComplete && (isDelivery ? isValid : (watchedValues.name && watchedValues.email && watchedValues.email.includes('@')))
  });

  // Handle card element change
  const handleCardChange = (event) => {
    console.log('[PaymentForm] Card element change event:', {
      complete: event.complete,
      error: event.error,
      brand: event.brand,
      empty: event.empty,
      focused: event.focused
    });
    
    setCardComplete(event.complete);
  };

  // Handle form submission
  const onSubmit = async (data) => {
    console.log('[PaymentForm] onSubmit called with data:', data);
    console.log('[PaymentForm] onSubmit check:', { stripe: !!stripe, elements: !!elements, cardComplete, hasPaymentIntent: !!paymentIntent });
    
    if (!stripe || !elements || !cardComplete) {
      console.log('[PaymentForm] onSubmit blocked by missing requirements');
      return;
    }

    // Ensure we have a payment intent
    if (!paymentIntent) {
      setError('Payment is not ready yet. Please wait a moment and try again.');
      return;
    }
    
    // For pickup orders, bypass Zod validation if we have the essential fields
    if (!isDelivery && !(data.name && data.email && data.email.includes('@'))) {
      setError('Please fill in your name and a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Debug: Check authentication status
      const user = auth.currentUser;
      console.log('[PaymentForm] Current user:', user ? {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified
      } : 'No user');
      
      if (!user) {
        throw new Error('User not authenticated. Please sign in and try again.');
      }

      console.log('[PaymentForm] Using existing payment intent:', {
        id: paymentIntent.id,
        amount: paymentIntent.amount,
        hasClientSecret: !!paymentIntent.client_secret,
        status: paymentIntent.status
      });

      // Validate that we have a client_secret
      if (!paymentIntent.client_secret) {
        throw new Error('Payment intent missing client_secret. Please try again.');
      }

      // Get card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment method
      console.log('[PaymentForm] Creating payment method with billing details:', data);
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: data,
      });

      if (paymentMethodError) {
        console.error('[PaymentForm] Payment method error:', paymentMethodError);
        throw new Error(paymentMethodError.message);
      }

      console.log('[PaymentForm] Payment method created:', paymentMethod.id);

      // Process payment - use the existing payment intent
      console.log('[PaymentForm] Processing payment with method:', paymentMethod.id);
      console.log('[PaymentForm] Confirming payment intent:', {
        id: paymentIntent.id,
        clientSecretPrefix: paymentIntent.client_secret ? paymentIntent.client_secret.substring(0, 20) + '...' : null,
        amount: paymentIntent.amount
      });
      const result = await processPayment(paymentMethod.id, data, paymentIntent);
      
      if (result.success) {
        console.log('[PaymentForm] Payment successful, order ID:', result.orderId);
        onPaymentSuccess(result.orderId);
      } else {
        console.error('[PaymentForm] Payment failed:', result.error);
        setError(result.error);
        onPaymentError(result.error);
      }
    } catch (err) {
      console.error('[PaymentForm] Payment error:', err);
      
      // Check if the error is an HTML response (server error)
      let errorMessage = err.message || 'Payment failed. Please try again.';
      
      if (err.message && err.message.includes('<!DOCTYPE')) {
        errorMessage = 'Server error: Payment service is temporarily unavailable. Please try again later.';
      } else if (err.message && err.message.includes('Unexpected token')) {
        errorMessage = 'Server error: Invalid response from payment service. Please try again later.';
      }
      
      setError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Card element options
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#222222',
        fontFamily: 'Montserrat, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#df1b41',
        iconColor: '#df1b41',
      },
    },
  };

  // Check if form is complete
  // For pickup orders, we don't need address validation
  const isFormComplete = cardComplete && (isDelivery ? isValid : (watchedValues.name && watchedValues.email && watchedValues.email.includes('@')));

  // Show loading state if payment intent is being created
  if (creatingIntent || !paymentIntent) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C97B53]"></div>
        <p className="text-sm text-gray-600">Initializing payment...</p>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit(onSubmit)} 
      className="space-y-6"
      onClick={() => console.log('[PaymentForm] Form clicked')}
    >
      {/* Billing Information */}
      <div className="space-y-4">
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              {...register('name')}
              className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
        </div>

        {/* Address Fields - Only show for delivery */}
        {isDelivery && (
          <div className="space-y-4">
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Street Address
              </label>
              <input
                type="text"
                id="address"
                {...register('address.line1')}
                className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent ${
                  errors.address?.line1 ? 'border-red-300' : 'border-gray-300'
                }`}
                required
              />
              {errors.address?.line1 && (
                <p className="mt-1 text-sm text-red-600">{errors.address.line1.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  {...register('address.city')}
                  className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent ${
                    errors.address?.city ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.address?.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.city.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  {...register('address.state')}
                  className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent ${
                    errors.address?.state ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.address?.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.state.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700">
                  ZIP Code
                </label>
                <input
                  type="text"
                  id="postal_code"
                  {...register('address.postal_code')}
                  className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent ${
                    errors.address?.postal_code ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.address?.postal_code && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.postal_code.message}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Information */}
      <div className="space-y-4">
        
        <div>
          <label htmlFor="card-element" className="block text-sm font-medium text-gray-700">
            Card Details
          </label>
          <div className="mt-1">
            <CardElement
              id="card-element"
              options={cardElementOptions}
              onChange={handleCardChange}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent"
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Your payment information is secure and encrypted by Stripe.
          </p>
        </div>
      </div>

      {/* Digital Wallet Options */}
      {canMakePayment && (
        <div className="space-y-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or pay with</span>
            </div>
          </div>

          <div className="space-y-3">
            {/* Only render Apple Pay on Safari environments where it's actually supported */}
            {applePayAvailable && (
              <button
                type="button"
                onClick={handlePaymentRequest}
                disabled={walletLoading || loading}
                className="w-full flex justify-center items-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-black text-white text-sm font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C97B53] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {walletLoading ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.22 2.65 4.36 2.68 4.36-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    Pay with Apple Pay
                  </>
                )}
              </button>
            )}

            {googlePayAvailable && (
              <button
                type="button"
                onClick={handlePaymentRequest}
                disabled={walletLoading || loading}
                className="w-full flex justify-center items-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C97B53] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {walletLoading ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Pay with Google Pay
                  </>
                )}
              </button>
            )}
          </div>

          {walletError && (
            <div className="text-red-600 text-sm text-center">{walletError}</div>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Payment Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="button"
        disabled={!stripe || !isFormComplete || loading}
        className="w-full bg-gradient-to-r from-[#C97B53] to-[#E89B5A] text-white py-3 px-4 rounded-md font-medium text-sm hover:from-[#B86A42] hover:to-[#D88A4A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C97B53] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        onClick={() => {
          console.log('[PaymentForm] Manual button click - calling onSubmit directly');
          const formData = {
            name: watchedValues.name,
            email: watchedValues.email,
            address: watchedValues.address
          };
          onSubmit(formData);
        }}
      >
        {loading ? 'Processing Payment...' : `Pay $${orderData.total.toFixed(2)}`}
      </button>

      {/* Security Notice */}
      <div className="text-center text-sm text-gray-500">
        <p>🔒 Your payment is secured by Stripe</p>
        <p>We never store your payment information</p>
      </div>
    </form>
  );
}
